import { useState } from "react";
import {
  Globe,
  Lock,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Star,
} from "lucide-react";
import {
  useConversationViews,
  getColorOption,
} from "../../context/ConversationViewsContext";
import type { ConversationViewResponse, ConversationViewCreateRequest } from "../../gen/models";
import { ViewModal } from "./ViewModal";
import { DeleteViewModal } from "./DeleteViewModal";
import { useLocation } from "react-router-dom";

interface ConversationViewsNavProps {
  onCloseMobile?: () => void;
}

export function ConversationViewsNav({ onCloseMobile }: ConversationViewsNavProps) {
  const {
    views,
    loading,
    activeViewId,
    applyView,
    createView,
    updateView,
    deleteView,
    toggleDefaultView,
  } = useConversationViews();

  const location = useLocation();
  const isThreadsRoute = location.pathname === "/threads" || location.pathname.startsWith("/threads/");

  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingView, setEditingView] = useState<ConversationViewResponse | null>(null);
  const [deletingView, setDeletingView] = useState<ConversationViewResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectView = (view: ConversationViewResponse) => {
    applyView(view);
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  const handleCreateSubmit = async (data: ConversationViewCreateRequest) => {
    setIsSubmitting(true);
    try {
      await createView(data);
      setIsCreateModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (data: ConversationViewCreateRequest) => {
    if (!editingView) return;
    setIsSubmitting(true);
    try {
      await updateView(editingView.id, {
        name: data.name,
        color: data.color,
        is_global: data.is_global,
        filters: data.filters,
      });
      setEditingView(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingView) return;
    setIsSubmitting(true);
    try {
      await deleteView(deletingView.id);
      setDeletingView(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-1 mb-2">
      {/* Section Header */}
      <div className="flex items-center justify-between px-3 py-1 text-[11px] font-semibold text-stone-500 dark:text-stone-400 select-none">
        <button
          type="button"
          onClick={() => setIsSectionOpen((prev) => !prev)}
          className="flex items-center gap-1.5 hover:text-stone-800 dark:hover:text-stone-200 transition"
          title={isSectionOpen ? "Collapse views" : "Expand views"}
        >
          {isSectionOpen ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
          <span className="uppercase tracking-wider text-[10px] font-bold">Views</span>
          <span className="px-1 py-0.2 rounded-full bg-stone-200/80 dark:bg-stone-800 text-[9px] font-mono font-medium text-stone-600 dark:text-stone-400">
            {views.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="p-1 rounded-md hover:bg-stone-200/70 dark:hover:bg-stone-800 hover:text-stone-900 dark:hover:text-stone-100 transition"
          title="Create new view"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Views List */}
      {isSectionOpen && (
        <div className="space-y-0.5 px-1.5 mt-0.5">
          {loading && views.length === 0 ? (
            <div className="px-3 py-1.5 text-[11px] text-stone-400 dark:text-stone-500 animate-pulse">
              Loading views...
            </div>
          ) : views.length === 0 ? (
            <div className="px-3 py-2 text-center">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="text-[11px] text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition underline underline-offset-2"
              >
                + Create first view
              </button>
            </div>
          ) : (
            views.map((view) => {
              const colorOpt = getColorOption(view.color);
              const isActive = isThreadsRoute && activeViewId === view.id;

              return (
                <div
                  key={view.id}
                  className={`group relative flex items-center justify-between px-2.5 h-7 rounded-lg text-xs transition border-l-[3.5px] ${
                    colorOpt.borderClass
                  } ${
                    isActive
                      ? "bg-stone-100 dark:bg-stone-800 text-stone-900 dark:text-stone-100 font-semibold shadow-2xs"
                      : "text-stone-600 dark:text-stone-400 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 hover:text-stone-900 dark:hover:text-stone-200"
                  }`}
                >
                  {/* View Clickable Target */}
                  <button
                    type="button"
                    onClick={() => handleSelectView(view)}
                    className="flex-1 min-w-0 text-left truncate flex items-center gap-1.5 mr-1"
                    title={`${view.name} (${view.is_global ? "Global" : "Private"})`}
                  >
                    <span className="truncate">{view.name}</span>
                  </button>

                  {/* Scope Icon, Default Star & Actions */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    {/* Default Star indicator when not hovering */}
                    {view.is_default && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDefaultView(view.id);
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded text-amber-500 hover:text-amber-600 transition"
                        title="Your default view (click to unset)"
                      >
                        <Star className="w-3 h-3 fill-amber-400" />
                      </button>
                    )}

                    {/* Default scope indicator */}
                    <div className="w-5 h-5 flex items-center justify-center text-stone-400 dark:text-stone-500 group-hover:hidden">
                      {view.is_global ? (
                        <span title="Global view"><Globe className="w-3 h-3" /></span>
                      ) : (
                        <span title="Private view"><Lock className="w-3 h-3" /></span>
                      )}
                    </div>

                    {/* Hover actions (Toggle Default, Rename / Edit, Delete) */}
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      {!view.is_default && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDefaultView(view.id);
                          }}
                          className="w-5 h-5 flex items-center justify-center rounded text-stone-400 hover:text-amber-500 hover:bg-stone-200/80 dark:hover:bg-stone-700 transition"
                          title="Set as your default view"
                        >
                          <Star className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingView(view);
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-200/80 dark:hover:bg-stone-700 transition"
                        title="Rename / Edit view"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingView(view);
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded text-stone-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/60 transition"
                        title="Delete view"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create View Modal */}
      <ViewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Edit / Rename View Modal */}
      <ViewModal
        isOpen={Boolean(editingView)}
        initialView={editingView}
        onClose={() => setEditingView(null)}
        onSubmit={handleUpdateSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <DeleteViewModal
        isOpen={Boolean(deletingView)}
        view={deletingView}
        onClose={() => setDeletingView(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isSubmitting}
      />
    </div>
  );
}
